// What goes inside the QR code for each type. Kept free of React so it can be
// unit-tested and reused (organizer pages, mobile).

// Wi-Fi strings escape \ ; , : and " (ZXing / iOS / Android convention).
const wifiEscape = (s = "") => String(s).replace(/([\\;,:"])/g, "\\$1");
// vCard 3.0 text values escape \ , ; and newlines.
const vEscape = (s = "") =>
  String(s).replace(/\\/g, "\\\\").replace(/([,;])/g, "\\$1").replace(/\r?\n/g, "\\n");

export const QR_TYPES = [
  { id: "url", label: "Website" },
  { id: "event", label: "InstaPass event" },
  { id: "wifi", label: "Wi-Fi" },
  { id: "vcard", label: "Contact card" },
  { id: "email", label: "Email" },
  { id: "sms", label: "Text message" },
  { id: "phone", label: "Phone call" },
  { id: "text", label: "Plain text" },
];

export const EMPTY_FIELDS = {
  url: { url: "" },
  event: { url: "" },
  wifi: { ssid: "", password: "", security: "WPA", hidden: false },
  vcard: { first: "", last: "", org: "", title: "", phone: "", email: "", website: "", address: "" },
  email: { to: "", subject: "", body: "" },
  sms: { phone: "", message: "" },
  phone: { phone: "" },
  text: { text: "" },
};

export function normalizeUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return s;
  return `https://${s}`;
}

function isHttpUrl(s) {
  try {
    const u = new URL(s);
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".");
  } catch {
    return false;
  }
}

const cleanPhone = (p = "") => String(p).replace(/[^\d+]/g, "");

/** Returns { payload, error }. payload is "" until the form is usable. */
export function buildPayload(type, f = {}) {
  switch (type) {
    case "url":
    case "event": {
      const url = normalizeUrl(f.url);
      if (!url) return { payload: "", error: "" };
      if (!isHttpUrl(url)) return { payload: "", error: "Enter a full web address, like instapass.shop/…" };
      if (type === "event" && !/(^|\.)instapass\.(shop|ai)$/i.test(new URL(url).hostname))
        return { payload: url, error: "", warning: "This isn't an InstaPass link — it will still work." };
      return { payload: url, error: "" };
    }
    case "wifi": {
      if (!f.ssid) return { payload: "", error: "" };
      const T = f.security === "nopass" ? "nopass" : f.security;
      let s = `WIFI:T:${T};S:${wifiEscape(f.ssid)};`;
      if (T !== "nopass") {
        if (!f.password) return { payload: "", error: "Add the Wi-Fi password, or choose “No password”." };
        s += `P:${wifiEscape(f.password)};`;
      }
      if (f.hidden) s += "H:true;";
      return { payload: s + ";", error: "" };
    }
    case "vcard": {
      if (!f.first && !f.last && !f.org) return { payload: "", error: "" };
      const lines = [
        "BEGIN:VCARD",
        "VERSION:3.0",
        `N:${vEscape(f.last)};${vEscape(f.first)};;;`,
        `FN:${vEscape([f.first, f.last].filter(Boolean).join(" ") || f.org)}`,
      ];
      if (f.org) lines.push(`ORG:${vEscape(f.org)}`);
      if (f.title) lines.push(`TITLE:${vEscape(f.title)}`);
      if (f.phone) lines.push(`TEL;TYPE=CELL:${cleanPhone(f.phone)}`);
      if (f.email) lines.push(`EMAIL:${String(f.email).trim()}`);
      if (f.website) lines.push(`URL:${normalizeUrl(f.website)}`);
      if (f.address) lines.push(`ADR;TYPE=WORK:;;${vEscape(f.address)};;;;`);
      lines.push("END:VCARD");
      return { payload: lines.join("\r\n"), error: "" };
    }
    case "email": {
      const to = String(f.to || "").trim();
      if (!to) return { payload: "", error: "" };
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) return { payload: "", error: "That email address doesn't look right." };
      const q = [];
      if (f.subject) q.push(`subject=${encodeURIComponent(f.subject)}`);
      if (f.body) q.push(`body=${encodeURIComponent(f.body)}`);
      return { payload: `mailto:${to}${q.length ? `?${q.join("&")}` : ""}`, error: "" };
    }
    case "sms": {
      const p = cleanPhone(f.phone);
      if (!p) return { payload: "", error: "" };
      return { payload: `SMSTO:${p}:${f.message || ""}`, error: "" };
    }
    case "phone": {
      const p = cleanPhone(f.phone);
      if (!p) return { payload: "", error: "" };
      return { payload: `tel:${p}`, error: "" };
    }
    case "text": {
      const t = String(f.text || "");
      return { payload: t.trim() ? t : "", error: "" };
    }
    default:
      return { payload: "", error: "" };
  }
}

/** Default file name for downloads. */
export function suggestName(type, f = {}) {
  const base =
    (type === "wifi" && f.ssid) ||
    (type === "vcard" && [f.first, f.last].filter(Boolean).join("-")) ||
    ((type === "url" || type === "event") && f.url && normalizeUrl(f.url).replace(/^https?:\/\//, "").split(/[/?#]/)[0]) ||
    type;
  return `instapass-qr-${String(base).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "code"}`;
}
